import * as _ from 'lodash';
import {
  collectRuleReferencedKeys,
  getFormComponentKeys,
  getRawValidationRule,
} from '../Components/Validation';

// Provenance embedded on a copied rule. FormIO ignores unknown custom properties on a
// properties.validation[] entry, so this does not affect form behavior.
export interface ICopyAudit {
  sourceForm: string;
  sourceVersion: number;
  targetForm: string;
  copiedByUtc: string;
  note?: string;
}

export interface IReferenceIntegrityResult {
  ok: boolean;
  missingKeys: string[];
}

export interface IMergeResult {
  form: any;
  overwritten: boolean;
}

// Returns the raw properties.validation[] rule for the given summary index.
export const extractRule = (sourceForm: any, ruleIndex: number): any =>
  getRawValidationRule(sourceForm, ruleIndex);

// Verifies every component key the rule references (target + logic formComponent refs +
// message errorComponents) exists in the destination form. Copies are blocked when any are missing.
export const checkReferenceIntegrity = (targetForm: any, rule: any): IReferenceIntegrityResult => {
  const available = getFormComponentKeys(targetForm);
  const referenced = collectRuleReferencedKeys(rule);
  // 'form' is the synthetic root entry always present; never treat it as missing.
  const missingKeys = referenced.filter(key => key !== 'form' && !available.has(key));
  return { ok: missingKeys.length === 0, missingKeys };
};

// Attaches/refreshes the provenance block on a (cloned) rule without mutating the original.
export const attachCopyAudit = (rule: any, audit: ICopyAudit): any => ({
  ...rule,
  _copyAudit: audit,
});

// Stable identity for overwrite-vs-append, consistent with the compare view's keyOf (target + description).
const ruleKey = (rule: any): string => `${_.get(rule, 'target', '')}\u0001${_.get(rule, 'description', '')}`;

// Merges a rule into a (deep-cloned) copy of the target form. If a rule with the same
// target+description already exists it is overwritten; otherwise the rule is appended.
export const mergeRuleIntoForm = (targetForm: any, rule: any): IMergeResult => {
  const form = _.cloneDeep(targetForm);
  const existing: any[] = Array.isArray(_.get(form, 'properties.validation'))
    ? _.get(form, 'properties.validation')
    : [];
  const key = ruleKey(rule);
  const matchIndex = existing.findIndex(r => ruleKey(r) === key);

  let overwritten = false;
  if (matchIndex >= 0) {
    existing[matchIndex] = rule;
    overwritten = true;
  } else {
    existing.push(rule);
  }

  _.set(form, 'properties.validation', existing);
  return { form, overwritten };
};

// Full copy pipeline: extract -> integrity check -> audit -> merge. Throws with the missing keys
// when reference integrity fails so the caller can block and display them.
export const buildCopiedForm = (
  sourceForm: any,
  ruleIndex: number,
  targetForm: any,
  audit: ICopyAudit,
): IMergeResult => {
  const rule = extractRule(sourceForm, ruleIndex);
  if (!rule) {
    throw new Error('The selected validation rule could not be found in the source form.');
  }

  const integrity = checkReferenceIntegrity(targetForm, rule);
  if (!integrity.ok) {
    throw new ReferenceIntegrityError(integrity.missingKeys);
  }

  const auditedRule = attachCopyAudit(rule, audit);
  return mergeRuleIntoForm(targetForm, auditedRule);
};

export class ReferenceIntegrityError extends Error {
  public readonly missingKeys: string[];

  constructor(missingKeys: string[]) {
    super(`The rule references components that do not exist in the destination form: ${missingKeys.join(', ')}`);
    this.name = 'ReferenceIntegrityError';
    this.missingKeys = missingKeys;
    Object.setPrototypeOf(this, ReferenceIntegrityError.prototype);
  }
}
